import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Offcanvas, Form, Button } from "react-bootstrap";
import { Input, Switch } from "antd";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import { showToast } from "../../../../core/data/redux/slices/ToastSlice";
import api from "../../../../core/axios/axiosInstance";

const EditCompanyOffcanvas = ({ visible, onClose, company }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: company.email || "",
    extensionNumber: company.extensionNumber || "",
    telephone: company.telephone || "",
    extensionStatus:
      company?.extensionStatus === true ||
      company?.extensionStatus === "active",
    accountStatus: company?.accountStatus === "active",
  });

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // If extension status is active, ensure both fields are present
      if (formData.extensionStatus) {
        if (!formData.extensionNumber || !formData.telephone) {
          dispatch(
            showToast({
              heading: "Error",
              message:
                "To activate extension status, please fill Extension Number and Telephone.",
              variant: "danger",
            })
          );
          setLoading(false);
          return;
        }
      }

      const payload = {
        userId: company._id,
        extensionNumber: formData.extensionNumber,
        telephone: formData.telephone,
        status: formData.extensionStatus,
      };

      // Only include email if it has changed
      if (formData.email !== company.email) {
        payload.newEmail = formData.email;
      }

      const response = await api.put(
        "/superAdmin/editCompanyAdminAndAgent",
        payload
      );

      // Update account status separately if changed
      const currentAccountStatus = company?.accountStatus === "active";
      if (formData.accountStatus !== currentAccountStatus) {
        const endpoint = formData.accountStatus
          ? "/deleteUser/activate"
          : "/deleteUser/suspend";
        await api.post(endpoint, {
          user_id: company._id,
        });
      }

      if (response.data.status === "success") {
        const message = response.data.emailVerificationSent
          ? "Company admin updated successfully. A verification email has been sent to the new email address."
          : "Company admin updated successfully";

        dispatch(
          showToast({
            heading: "Success",
            message: message,
            variant: "success",
          })
        );
        onClose(true);
      }
    } catch (error) {
      dispatch(
        showToast({
          heading: "Error",
          message:
            error?.response?.data?.error || "Failed to update company admin",
          variant: "danger",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Offcanvas
      show={visible}
      onHide={onClose}
      placement="end"
      style={{ width: "500px" }}
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>
          Edit Company Admin -{" "}
          {company.firstname
            ? `${company.firstname} ${company.lastname}`
            : "N/A"}
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <Form onSubmit={handleSubmit}>
          <div
            className="mb-3 p-3"
            style={{ backgroundColor: "#f5f5f5", borderRadius: "8px" }}
          >
            <div className="mb-2">
              <strong>Name:</strong>{" "}
              {company.firstname
                ? `${company.firstname} ${company.lastname}`
                : "N/A"}
            </div>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>
              Email <span className="text-danger">*</span>
            </Form.Label>
            <Input
              type="email"
              placeholder="Enter email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              required
            />
            <div className="form-text text-muted" style={{ marginTop: 6 }}>
              If email is changed, a verification email will be sent to the new
              address.
            </div>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Extension Number <span className="text-danger">*</span>
            </Form.Label>
            <Input
              placeholder="Enter extension number"
              value={formData.extensionNumber}
              onChange={(e) =>
                handleInputChange("extensionNumber", e.target.value)
              }
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Telephone</Form.Label>
            <PhoneInput
              country={"ae"}
              value={formData.telephone || ""}
              onChange={(value) => handleInputChange("telephone", value)}
              enableSearch
              inputProps={{ name: "telephone" }}
              inputStyle={{ width: "100%" }}
              containerStyle={{ width: "100%" }}
            />
          </Form.Group>

          <div className="mb-2 text-muted">
            Note: To activate extension status, both Extension Number and
            Telephone must be filled.
          </div>

          <Form.Group className="mb-3">
            <Form.Label>Extension Status</Form.Label>
            <div>
              <Switch
                checked={formData.extensionStatus}
                onChange={(checked) =>
                  handleInputChange("extensionStatus", checked)
                }
                checkedChildren="Active"
                unCheckedChildren="Inactive"
              />
            </div>
            <div className="form-text text-muted mt-2">
              When extension status is inactive, user can login but cannot use
              call functionality.
            </div>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Account Status</Form.Label>
            <div>
              <Switch
                checked={formData.accountStatus}
                onChange={(checked) =>
                  handleInputChange("accountStatus", checked)
                }
                checkedChildren="Active"
                unCheckedChildren="Suspended"
              />
            </div>
            <div className="form-text text-muted mt-2">
              When account status is suspended, user cannot login to the system.
            </div>
          </Form.Group>

          <div className="d-flex justify-content-end gap-2">
            <Button variant="secondary" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </Form>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default EditCompanyOffcanvas;
